import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import environment from '../config/environment';
import apiService from '../services/api';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { signOut } from '../store/authSlice';
import { ResearchConfig, ResearchSummary } from '../types/study';
import './research.css';
import { DistributionChart, FrequencyChart, BoxPlotChart } from '../components/research/ResearchFigures';

const COPY = {
  administration: 'Administración',
  eyebrow: 'Consola de investigación', title: 'Loftus & Palmer · Protocolo experimental',
  subtitle: 'Monitoreo descriptivo e inferencial de respuestas anónimas.',
  logout: 'Cerrar sesión', loading: 'Calculando resultados…', retry: 'Reintentar',
  forbidden: 'Tu cuenta está autenticada, pero no tiene permisos de investigador.',
  error: 'No fue posible cargar el panel. Inténtalo nuevamente.',
  refreshWarning: 'No pudimos actualizar los datos. Se muestran los últimos resultados disponibles y volveremos a intentarlo automáticamente.',
  total: 'Participantes', completed: 'Completaron', completion: 'Finalización', updated: 'Corte estadístico',
  emptyTitle: 'Aún no hay respuestas completas', emptyBody: 'El panel mostrará estadísticas cuando al menos una persona complete el protocolo.',
  distribution: 'Distribución por condición', descriptive: 'Estadística descriptiva', frequency: 'Frecuencias de velocidad', boxplot: 'Diagrama de caja', inference: 'ANOVA de una vía',
  condition: 'Condición', n: 'n', percentage: 'Proporción', mean: 'Media', sd: 'Desv. estándar', ci: 'IC 95 %',
  min: 'Mín.', q1: 'Q1', median: 'Mediana', q3: 'Q3', max: 'Máx.',
  insufficient: 'Se necesitan respuestas en al menos dos condiciones para calcular ANOVA y tamaño del efecto.',
  fStatistic: 'Estadístico F', pValue: 'Valor p', effect: 'η² (tamaño del efecto)', degrees: 'Grados de libertad',
  exports: 'Datos y figuras', exportBody: 'Descarga la base anonimizada completa o conserva cada figura como SVG editable.',
  csv: 'Descargar CSV', xlsx: 'Descargar Excel', preparing: 'Preparando archivo…', downloadError: 'No pudimos preparar la descarga.', ready: 'Archivo descargado correctamente.', downloadFigure: 'Descargar SVG',
  paused: 'La recepción de nuevas participaciones está pausada.',
  configured: 'Material habilitado', blocked: 'Protocolo bloqueado', questions: 'Preguntas activas', conditions: 'Condiciones de asignación',
  videos: 'Asignaciones por video', video: 'Video', videoStatus: 'Estado', videoActive: 'Activo', videoArchived: 'Archivado',
  speedEstimate: 'Velocidad estimada',
  responseFrequencies: 'Frecuencias de respuestas',
  responseFrequenciesDescription: 'Solo sesiones completadas. Cada total elegible cuenta a quienes recibieron esa versión de la pregunta. Respondieron + sin respuesta = elegibles; las respuestas «No» y 0 cuentan como respuestas. Los porcentajes usan el total elegible. En selección múltiple se cuentan combinaciones completas.',
  memory: 'Memoria', falseMemory: 'Falsas memorias',
  responseFrequenciesEmpty: 'Aún no hay sesiones completadas con preguntas de esta sección.',
  frequencies: 'frecuencias', version: 'Versión', eligible: 'Elegibles', responded: 'respondieron', missing: 'sin respuesta',
  response: 'Respuesta', responseFrequency: 'Frecuencia', eligiblePercentage: '% de elegibles',
  missingResponse: 'Sin respuesta', totalEligible: 'Total elegible', fullPercentage: '100 %',
  downloadResponseFrequencies: 'Descargar frecuencias CSV',
  responseFrequenciesExportDescription: 'Estas mismas tablas se incluyen en la hoja Resumen de Excel. La base CSV completa conserva también las sesiones pendientes y retiradas.',
  protocolReady: 'El protocolo está habilitado para recibir participantes.',
  protocolBlocked: 'El protocolo público está bloqueado hasta configurar y autorizar el video.',
  noExport: 'Las descargas se habilitan cuando exista al menos un registro.',
  logoutPending: 'Cerrando sesión…', logoutError: 'No fue posible cerrar la sesión. Inténtalo nuevamente.',
  tableCaption: 'Media, desviación estándar e intervalo de confianza por condición. Solo respuestas críticas de sesiones completadas. DE muestral; IC95 bilateral Student t (n−1 gl) para todo tamaño de muestra. Con N<2 no se estiman DE ni IC. ANOVA supone independencia, normalidad residual y varianzas homogéneas; estos supuestos no se comprueban automáticamente.',
};

export const RESEARCH_REFRESH_INTERVAL_MS = Math.min(5000, Math.max(500, environment.pollingInterval || 2000));
const format = (value: number | null | undefined, digits = 2) => value == null ? '—' : new Intl.NumberFormat('en-US', { useGrouping: false, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);

function getError(error: unknown, fallback: string) {
  const candidate = error as { response?: { status?: number; data?: { detail?: string } } };
  return { status: candidate.response?.status, message: candidate.response?.data?.detail || fallback };
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const ResearchDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [summary, setSummary] = useState<ResearchSummary | null>(null);
  const [config, setConfig] = useState<ResearchConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshWarning, setRefreshWarning] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [downloading, setDownloading] = useState<'csv' | 'xlsx' | 'frequencies' | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  useEffect(() => {
    let active = true;
    let initialized = false;
    let timer: number | undefined;
    let controller: AbortController | null = null;

    const refresh = async () => {
      controller = new AbortController();
      let keepPolling = true;
      if (!initialized) {
        setLoading(true); setError(''); setForbidden(false);
      }
      try {
        const requestConfig = { signal: controller.signal };
        const [summaryResponse, configResponse] = initialized
          ? [await apiService.getResearchSummary(requestConfig), null]
          : await Promise.all([
            apiService.getResearchSummary(requestConfig),
            apiService.getResearchConfig(requestConfig),
          ]);
        if (!active) return;
        setSummary(summaryResponse.data);
        if (configResponse) {
          setConfig(configResponse.data);
        }
        initialized = true;
        setError(''); setForbidden(false); setRefreshWarning('');
      } catch (requestError) {
        if (!active) return;
        const resolved = getError(requestError, COPY.error);
        if (resolved.status === 403) {
          keepPolling = false;
          setForbidden(true); setError(COPY.forbidden); setRefreshWarning('');
        } else if (initialized) {
          setRefreshWarning(COPY.refreshWarning);
        } else {
          setError(resolved.message);
        }
      } finally {
        controller = null;
        if (active) {
          setLoading(false);
          if (keepPolling) timer = window.setTimeout(refresh, RESEARCH_REFRESH_INTERVAL_MS);
        }
      }
    };

    void refresh();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
      controller?.abort();
    };
  }, [reloadKey]);

  const download = async (kind: 'csv' | 'xlsx' | 'frequencies') => {
    setDownloading(kind); setDownloadStatus(''); setDownloadFailed(false);
    try {
      const response = await apiService.downloadResearchExport(kind === 'frequencies' ? 'csv' : kind, kind === 'frequencies' ? 'response_frequencies' : undefined);
      saveBlob(response.data, kind === 'frequencies' ? 'loftus-palmer-frecuencias-respuestas.csv' : `loftus-palmer-respuestas.${kind}`); setDownloadStatus(COPY.ready);
    } catch (requestError) {
      let message = getError(requestError, COPY.downloadError).message;
      const body = (requestError as { response?: { data?: unknown } }).response?.data;
      if (body instanceof Blob) {
        try {
          const details = JSON.parse(await body.text());
          if (typeof details.detail === 'string') message = details.detail;
        } catch { /* A non-JSON download failure keeps the contextual fallback. */ }
      }
      setDownloadFailed(true);
      setDownloadStatus(message);
    }
    finally { setDownloading(null); }
  };

  const logout = async () => {
    setLoggingOut(true); setLogoutError('');
    try {
      await dispatch(signOut()).unwrap();
      navigate('/login');
    } catch {
      setLogoutError(COPY.logoutError);
    } finally {
      setLoggingOut(false);
    }
  };

  if (loading) return <main className="rd-state" role="status">{COPY.loading}</main>;
  if (error) return <main className="rd-state" data-testid="research-error" role="alert"><h1>{error}</h1>{!forbidden && <button onClick={() => setReloadKey((key) => key + 1)}>{COPY.retry}</button>}</main>;
  if (!summary || !config) return null;
  const frequencySections = (['memory', 'false_memory'] as const).map((category) => ({
    category,
    groups: (summary.response_frequencies || []).filter((group) => group.category === category),
  }));
  const completionRate = summary.total_participants ? (summary.completed_participants / summary.total_participants) * 100 : 0;

  return <div className="rd-shell" data-testid="research-dashboard">
    <header className="rd-header"><div><p>{COPY.eyebrow}</p><h1>{COPY.title}</h1><span>{COPY.subtitle}</span></div><div className="rd-header__actions"><span>{user?.email}</span><button onClick={() => navigate('/investigacion/administracion')}>{COPY.administration}</button><button onClick={logout} disabled={loggingOut}>{loggingOut ? COPY.logoutPending : COPY.logout}</button>{logoutError && <span className="rd-header__error" role="alert">{logoutError}</span>}</div></header>
    <main className="rd-main">
      {refreshWarning && <p className="rd-refresh-warning" data-testid="research-refresh-warning" role="status">{refreshWarning}</p>}
      <section className={`rd-protocol-status ${config.video.configured && config.active ? 'is-ready' : 'is-blocked'}`} role="status"><strong>{config.video.configured && config.active ? COPY.configured : COPY.blocked}</strong><span>{config.video.configured && config.active ? COPY.protocolReady : !config.active ? COPY.paused : config.blocked_reason || COPY.protocolBlocked}</span></section>

      <section className="rd-metrics" data-testid="summary-ready"><article><span>{COPY.total}</span><strong>{summary.total_participants}</strong></article><article><span>{COPY.completed}</span><strong>{summary.completed_participants}</strong></article><article><span>{COPY.completion}</span><strong>{format(completionRate, 1)}%</strong></article><article><span>{COPY.updated}</span><strong>{new Date(summary.generated_at).toLocaleDateString('es')}</strong></article></section>

      {summary.completed_participants === 0 ? <section className="rd-empty" data-testid="research-empty"><h2>{COPY.emptyTitle}</h2><p>{COPY.emptyBody}</p></section> : <>
        <section className="rd-grid"><DistributionChart summary={summary} unit={config.speed_unit} studyTitle={config.title} /><FrequencyChart summary={summary} unit={config.speed_unit} studyTitle={config.title} /></section>
        <section className="rd-panel"><h2>{COPY.descriptive}</h2><div className="rd-table-wrap" role="region" aria-label={COPY.descriptive} tabIndex={0}><table><caption>{`${COPY.tableCaption} ${COPY.speedEstimate} (${config.speed_unit}).`}</caption><thead><tr><th>{COPY.condition}</th><th>{COPY.n}</th><th>{COPY.mean}</th><th>{COPY.sd}</th><th>{COPY.ci}</th></tr></thead><tbody>{summary.descriptive.map((row) => <tr key={row.condition}><th>{row.verb}</th><td>{row.n}</td><td>{format(row.mean)}</td><td>{format(row.standard_deviation)}</td><td>{format(row.confidence_interval_95.lower)} — {format(row.confidence_interval_95.upper)}</td></tr>)}</tbody></table></div></section>
        <section className="rd-grid"><BoxPlotChart summary={summary} unit={config.speed_unit} studyTitle={config.title} /><article className="rd-inference"><p className="rd-kicker">{COPY.inference}</p>{summary.anova.available ? <><dl><div><dt>{COPY.fStatistic}</dt><dd>{summary.anova.f_statistic == null && summary.anova.reason ? '∞' : format(summary.anova.f_statistic, 3)}</dd></div><div><dt>{COPY.pValue}</dt><dd>{summary.anova.p_value != null && summary.anova.p_value > 0 && summary.anova.p_value < 0.0001 ? '< 0.0001' : format(summary.anova.p_value, 4)}</dd></div><div><dt>{COPY.effect}</dt><dd>{format(summary.anova.effect_size_eta_squared, 3)}</dd></div><div><dt>{COPY.degrees}</dt><dd>{summary.anova.df_between} / {summary.anova.df_within}</dd></div></dl>{summary.anova.reason && <p>{summary.anova.reason}</p>}</> : <p>{summary.anova.reason || COPY.insufficient}</p>}</article></section>
      </>}

      <section className="rd-panel" data-testid="video-distribution">
        <h2>{COPY.videos}</h2>
        <div className="rd-table-wrap" role="region" aria-label={COPY.videos} tabIndex={0}>
          <table>
            <thead><tr><th>{COPY.video}</th><th>{COPY.videoStatus}</th><th>{COPY.n}</th></tr></thead>
            <tbody>{(summary.video_distribution || []).map((video) => <tr key={video.id}>
              <th scope="row">{video.title}</th>
              <td>{video.active ? COPY.videoActive : COPY.videoArchived}</td>
              <td>{video.count}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>

      <section className="rd-panel" data-testid="response-frequencies">
        <h2>{COPY.responseFrequencies}</h2>
        <p>{COPY.responseFrequenciesDescription}</p>
        {frequencySections.map(({ category, groups }) => <div key={category}>
          <h3>{category === 'memory' ? COPY.memory : COPY.falseMemory}</h3>
          {groups.length === 0 && <p>{COPY.responseFrequenciesEmpty}</p>}
          {groups.map((group, index) => <div key={`${group.question_key}-${index}`}>
            <div className="rd-table-wrap" role="region" aria-label={`${group.prompt} · ${COPY.frequencies}`} tabIndex={0}>
              <table><caption>{`${group.prompt} · ${group.question_key} · ${COPY.version} ${group.question_version} · ${COPY.eligible}: ${group.eligible}; ${COPY.responded}: ${group.responded}; ${COPY.missing}: ${group.missing}.`}</caption>
                <thead><tr><th>{COPY.response}</th><th>{COPY.responseFrequency}</th><th>{COPY.eligiblePercentage}</th></tr></thead>
                <tbody>{group.values.map((value, valueIndex) => <tr key={valueIndex}><th scope="row">{value.label}</th><td>{value.count}</td><td>{format(value.percentage)} %</td></tr>)}
                  <tr><th scope="row">{COPY.missingResponse}</th><td>{group.missing}</td><td>{format(group.eligible ? 100 * group.missing / group.eligible : 0)} %</td></tr>
                  <tr><th scope="row">{COPY.totalEligible}</th><td>{group.eligible}</td><td>{COPY.fullPercentage}</td></tr>
                </tbody>
              </table>
            </div>
          </div>)}
        </div>)}
        <button data-testid="export-response-frequencies" onClick={() => download('frequencies')} disabled={downloading !== null || !(summary.response_frequencies || []).length}>{downloading === 'frequencies' ? COPY.preparing : COPY.downloadResponseFrequencies}</button>
        <p>{COPY.responseFrequenciesExportDescription}</p>
      </section>

      <section className="rd-panel rd-reference"><div><p className="rd-kicker">{COPY.conditions}</p><div className="rd-tags">
        {config.conditions.filter((item) => item.active).map((item) => React.createElement('span', { key: item.id }, item.verb))}
      </div></div><div><p className="rd-kicker">{COPY.questions}</p><strong>{config.questions.filter((item) => item.active).length}</strong></div></section>
      <section className="rd-export"><div><p className="rd-kicker">{COPY.exports}</p><h2>{COPY.exports}</h2><p>{summary.total_participants ? COPY.exportBody : COPY.noExport}</p></div><div><button data-testid="export-csv" onClick={() => download('csv')} disabled={downloading !== null || summary.total_participants === 0}>{downloading === 'csv' ? COPY.preparing : COPY.csv}</button><button data-testid="export-xlsx" onClick={() => download('xlsx')} disabled={downloading !== null || summary.total_participants === 0}>{downloading === 'xlsx' ? COPY.preparing : COPY.xlsx}</button>{downloadStatus && <p role={downloadFailed ? 'alert' : 'status'}>{downloadStatus}</p>}</div></section>
    </main>
  </div>;
};

export default ResearchDashboard;
